import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '../ui/button';
import { Clock, ListChecks } from 'lucide-react';
import logo from '../../images/LogohvidFinal.png';
import signupBg from '../../images/signup.png';
import { formatFacilityDisplayDate } from '../facilities/utils/utils';
import { useOnboarding } from '../../store/onboarding-context';

export default function WelcomeStep() {
    const { department } = useOnboarding();
    const { t } = useTranslation();
    const navigate = useNavigate();

    const formattedDate = department?.start_date
        ? formatFacilityDisplayDate(department.start_date)
        : null;

    return (
        <div
            className="flex min-h-screen w-full items-center justify-center bg-cover bg-center px-4 py-16"
            style={{ backgroundImage: `url(${signupBg})` }}
        >
            <div className="w-full max-w-3xl rounded-2xl border border-subtle bg-card px-8 py-12 text-center shadow-2xl sm:px-16 sm:py-14">
                <img
                    src={logo}
                    alt="NordInsight"
                    className="mx-auto mb-10 h-10"
                />

                <h1 className="text-4xl font-medium tracking-tight text-foreground">
                    {t('onboarding.welcome.title')}
                </h1>
                <p className="mt-4 text-lg text-muted-foreground">
                    {t('onboarding.welcome.subtitle')}
                </p>

                {formattedDate && (
                    <div className="mt-6 inline-flex items-center rounded-full border border-emerald-500/30 bg-emerald-500/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-wider text-emerald-400">
                        {t(
                            department?.isTrial === false
                                ? 'onboarding.welcome.goLiveBadge'
                                : 'onboarding.welcome.trialBadge',
                            { date: formattedDate }
                        )}
                    </div>
                )}

                <p className="mx-auto mt-6 max-w-lg text-sm text-muted-foreground">
                    {t('onboarding.welcome.description')}
                </p>

                <div className="mt-10 grid grid-cols-1 gap-4 text-left sm:grid-cols-2">
                    <div className="rounded-lg border border-subtle bg-muted p-6">
                        <div className="mb-4 flex items-center gap-2">
                            <Clock
                                className="h-4 w-4 text-emerald-400"
                                aria-hidden="true"
                            />
                            <span className="text-xs font-semibold uppercase tracking-wider text-emerald-400">
                                {t('onboarding.welcome.whatToExpect')}
                            </span>
                        </div>
                        <ul className="space-y-2 text-sm text-muted-foreground">
                            <li className="flex items-center gap-2">
                                <span className="h-1 w-1 rounded-full bg-muted-foreground" />
                                {t('onboarding.welcome.expectItems.time')}
                            </li>
                            <li className="flex items-center gap-2">
                                <span className="h-1 w-1 rounded-full bg-muted-foreground" />
                                {t('onboarding.welcome.expectItems.steps')}
                            </li>
                            <li className="flex items-center gap-2">
                                <span className="h-1 w-1 rounded-full bg-muted-foreground" />
                                {t('onboarding.welcome.expectItems.save')}
                            </li>
                        </ul>
                    </div>

                    <div className="rounded-lg border border-subtle bg-muted p-6">
                        <div className="mb-4 flex items-center gap-2">
                            <ListChecks
                                className="h-4 w-4 text-emerald-400"
                                aria-hidden="true"
                            />
                            <span className="text-xs font-semibold uppercase tracking-wider text-emerald-400">
                                {t('onboarding.welcome.whatYouNeed')}
                            </span>
                        </div>
                        <ul className="space-y-2 text-sm text-muted-foreground">
                            <li className="flex items-center gap-2">
                                <span className="h-1 w-1 rounded-full bg-muted-foreground" />
                                {t('onboarding.welcome.needItems.facilityDetails')}
                            </li>
                            <li className="flex items-center gap-2">
                                <span className="h-1 w-1 rounded-full bg-muted-foreground" />
                                {t('onboarding.welcome.needItems.userList')}
                            </li>
                            <li className="flex items-center gap-2">
                                <span className="h-1 w-1 rounded-full bg-muted-foreground" />
                                {t('onboarding.welcome.needItems.scannerDetails')}
                            </li>
                            <li className="flex items-center gap-2">
                                <span className="h-1 w-1 rounded-full bg-muted-foreground" />
                                {t('onboarding.welcome.needItems.policies')}
                            </li>
                        </ul>
                    </div>
                </div>

                <Button
                    size="lg"
                    className="mt-10 w-64"
                    onClick={() => navigate('/wizard/business-info')}
                >
                    {t('onboarding.welcome.getStarted')}
                </Button>
            </div>
        </div>
    );
}
